// iter152 — Single source of truth for the 12 canonical Level-1
// categories used across the entire app.
//
// ⚠️ DO NOT HARDCODE CATEGORIES — always import from lib/categories.js ⚠️
//
// Every page (Discover chip row, Map chip row + Menu sheet,
// FilterPlacesSheet, business editor, map pin colors, etc.) must derive
// its list from `CATEGORIES` below. The `slugs` array on each entry is
// the set of legacy backend category strings that map into this UI
// category — used by the startup DB migration and by client-side
// filtering to catch any residual legacy values that haven't been
// migrated yet.
//
// Icon names use the `ti-*` Tabler naming convention per user spec.
// The runtime UI resolves them to lucide-react equivalents via
// `getCategoryIcon()` below since the app already ships lucide-react.

import {
  Utensils,       // ti-tools-kitchen-2
  Wine,           // ti-glass-full
  Coffee,         // ti-coffee
  Cigarette,      // ti-smoking
  Music,          // ti-music
  Building2,      // ti-building-skyscraper
  Waves,          // ti-swimming
  Landmark,       // ti-building-monument
  MapPin,         // ti-map-2
  ShoppingBasket, // ti-basket
  ShoppingBag,    // ti-shopping-bag
  Scissors,       // ti-scissors
} from "lucide-react";

export const CATEGORIES = [
  { slug: "food",       en: "Food",       es: "Comida",         icon: "ti-tools-kitchen-2",     Icon: Utensils,       slugs: ["food", "restaurant", "paladar", "cafeteria"] },
  { slug: "bar",        en: "Bar",        es: "Bar",            icon: "ti-glass-full",          Icon: Wine,           slugs: ["bar"] },
  { slug: "cafe",       en: "Café",       es: "Café",           icon: "ti-coffee",              Icon: Coffee,         slugs: ["cafe", "café", "bakery", "coffee_shop"] },
  { slug: "cigar",      en: "Cigar",      es: "Tabaco",         icon: "ti-smoking",             Icon: Cigarette,      slugs: ["cigar", "cigar_lounge", "cigar_shop", "tabaco"] },
  { slug: "nightlife",  en: "Nightlife",  es: "Vida Nocturna",  icon: "ti-music",               Icon: Music,          slugs: ["nightlife", "club", "nightclub", "cabaret"] },
  { slug: "rooftop",    en: "Rooftop",    es: "Azotea",         icon: "ti-building-skyscraper", Icon: Building2,      slugs: ["rooftop", "rooftop_bar"] },
  { slug: "pool",       en: "Pool",       es: "Piscina",        icon: "ti-swimming",            Icon: Waves,          slugs: ["pool", "swimming"] },
  { slug: "tours",      en: "Tours",      es: "Tours",          icon: "ti-building-monument",   Icon: Landmark,       slugs: ["tours", "tour", "museum", "art_gallery", "monument"] },
  { slug: "activities", en: "Activities", es: "Actividades",    icon: "ti-map-2",               Icon: MapPin,         slugs: ["activities", "activity", "class", "workshop"] },
  { slug: "market",     en: "Market",     es: "Mercado",        icon: "ti-basket",              Icon: ShoppingBasket, slugs: ["market", "mercado"] },
  { slug: "tienda",     en: "Tienda",     es: "Tienda",         icon: "ti-shopping-bag",        Icon: ShoppingBag,    slugs: ["tienda", "shop", "store", "boutique"] },
  { slug: "beauty",     en: "Beauty",     es: "Belleza",        icon: "ti-scissors",            Icon: Scissors,       slugs: ["beauty", "salon", "barbershop", "spa"] },
];

// Reverse map — legacy backend slug → canonical Level-1 slug. Used by
// client-side filtering to handle any businesses whose `category` field
// hasn't been migrated yet on this backend.
export const LEGACY_SLUG_TO_CATEGORY = (() => {
  const m = {};
  for (const c of CATEGORIES) c.slugs.forEach((s) => { m[s.toLowerCase()] = c.slug; });
  return m;
})();

/** Resolve any (legacy or canonical) category slug to its Level-1
 *  category entry. Returns `null` if unmapped. */
export function resolveCategory(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().trim();
  const canonical = LEGACY_SLUG_TO_CATEGORY[key];
  if (!canonical) return null;
  return CATEGORIES.find((c) => c.slug === canonical) || null;
}

/** i18n picker for a category's display name. */
export function categoryLabel(cat, lang) {
  if (!cat) return "";
  return (lang === "es" ? cat.es : cat.en) || cat.en;
}

/** Legacy shim: existing pages read a `CATEGORY_CHIPS` array with an
 *  "All" entry at index 0 + `key` + `label` + `tKey`. Keep the shape
 *  stable so no consumer needs a rewrite. */
export const CATEGORY_CHIPS = [
  { key: "all", label: "All", tKey: "chip_all" },
  ...CATEGORIES.map((c) => ({
    key: c.slug,
    label: c.en,
    tKey: `map_cat_${c.slug}_en`, // useI18n().t() will fall back to EN string
    tKeyEn: `map_cat_${c.slug}_en`,
    tKeyEs: `map_cat_${c.slug}_es`,
    Icon: c.Icon,
  })),
];
